import { ComponentProps } from 'react';
import barclaysLogo from '../../../../../assets/images/logo-barclays.svg';
import blackrockLogo from '../../../../../assets/images/logo-blackrock.svg';
import hsbcLogo from '../../../../../assets/images/logo-hsbc.svg';
import seapointLogo from '../../../../../assets/images/logo-seapoint.svg';
import wealthKernelLogo from '../../../../../assets/images/logo-wealth-kernel.svg';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';

type AccountType = 'EXTERNAL' | 'SEAPOINT' | 'TREASURY';

type AccountCardConfig =
  | {
      accountType: Extract<AccountType, 'EXTERNAL'>;
      onCtaClick: () => void;
    }
  | {
      accountType: Extract<AccountType, 'SEAPOINT'>;
      isLegalEntityInUk: boolean;
    }
  | {
      accountType: Extract<AccountType, 'TREASURY'>;
    };

type AccountTypeCardProps = {
  config: AccountCardConfig;
};

export const AccountTypeCard = ({ config }: AccountTypeCardProps) => {
  const { logos, title, description, featuresIntro, features, ctaText, isCtaEnabled, onCtaClick } = getCardContent({
    config,
  });

  return (
    <Stack
      space={5}
      justify="start"
      align="stretch"
      className="relative h-[575px] overflow-clip rounded-xl border-[0.5px] border-seapoint-gray-border bg-white shadow-sm md:flex-1"
    >
      <Inline space={4} justify="center" className="header-gradient py-10">
        {logos.map((logo) => (
          <div key={logo.id} className="h-14 w-14 overflow-clip rounded-full bg-white">
            <img
              src={logo.src}
              alt=""
              loading="eager"
              fetchPriority="high"
              className="h-full w-full object-contain p-1"
            />
          </div>
        ))}
      </Inline>

      <Stack space={5} justify="between" className="flex-1 px-5 pb-5">
        <Stack space={5} justify="start" align="stretch" className="w-full">
          <Stack space={2} className="h-24">
            <h3 className="text-2xl font-semibold text-black">{title}</h3>

            <p className="text-sm font-medium text-seapoint-gray-text">{description}</p>
          </Stack>

          <Stack space={2}>
            <p className="text-sm font-medium text-black">{featuresIntro}</p>

            <Stack space={2} as="ul" className="list-none" role="list" aria-label="Account features">
              {features.map((feature) => (
                <Inline
                  key={feature}
                  as="li"
                  wrap="nowrap"
                  align="start"
                  role="listitem"
                  aria-label={feature}
                  className="ml-2 text-sm font-normal text-seapoint-gray-text before:mr-0.5 before:shrink-0 before:text-lg before:leading-none before:content-['•']"
                >
                  <span>{feature}</span>
                </Inline>
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Stack space={5} justify="between" align="stretch" className="w-full">
          {/* Fix on click */}
          <Button variant={isCtaEnabled ? 'default' : 'outline'} onClick={onCtaClick} disabled={!isCtaEnabled}>
            {ctaText}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

type GetCardContentArgs = {
  config: ComponentProps<typeof AccountTypeCard>['config'];
};

const getCardContent = ({ config }: GetCardContentArgs) => {
  const { accountType } = config;

  switch (accountType) {
    case 'EXTERNAL':
      return {
        logos: [
          { id: 'HSBC_LOGO', src: hsbcLogo },
          { id: 'BARCLAYS_LOGO', src: barclaysLogo },
        ],
        title: 'Connect an external account',
        description: 'Link your existing business accounts to Seapoint using Open Banking.',
        featuresIntro: 'Connecting an external account lets you:',
        features: [
          'View transactions from all your bank accounts in Seapoint',
          'Reconcile transactions in one place',
          'Stay multi-banked, with a unified financial view',
        ],
        ctaText: 'Connect Account',
        isCtaEnabled: true,
        onCtaClick: config.onCtaClick,
      };
    case 'SEAPOINT': {
      return {
        logos: [{ id: 'SEAPOINT_LOGO', src: seapointLogo }],
        title: 'Open a Seapoint account',
        description: config.isLegalEntityInUk
          ? 'A business account powered by Modulr, with funds safeguarded at the Bank of England.'
          : 'A business account powered by Modulr, with funds safeguarded with leading banks such as JP Morgan.',
        featuresIntro: 'A Seapoint business account will allow you to:',
        features: [
          'Transact with no limits on maximum balances or transaction value',
          'Create unlimited internal accounts',
          'Access 15+ global currencies',
        ],
        ctaText: 'Speak to our team to open an account',
        isCtaEnabled: false,
      };
    }
    case 'TREASURY':
      return {
        logos: [
          { id: 'BLACKROCK_LOGO', src: blackrockLogo },
          { id: 'WEALTH_KERNEL_LOGO', src: wealthKernelLogo },
        ],
        title: 'Open a treasury account',
        description: 'Put your idle cash to work by investing in short-term money market funds managed by BlackRock.',
        featuresIntro: 'With a Seapoint treasury Account, you can:',
        features: [
          'Keep capital in low-risk, short-term investments',
          'Earn up to 4.4% on GBP and 2.4% on EUR balances',
          'Get instant access to your funds',
        ],
        ctaText: 'Coming Soon',
        isCtaEnabled: false,
      };
    default:
      return exhaustiveCheck(accountType);
  }
};
